import * as yup from 'yup';

import i18n from 'locals/i18n';
import { CustomFieldType, TicketType } from 'utils/types';
import { CustomFieldFormProps } from './types';

export const defaultValues = {
	ticket_types_ids: [],
	custom_field: {
		name: '',
		description: '',
		only_buyer: 'buyerOnly',
		is_required: 'required',
		name_type: 'CUSTOM_QUESTION',
		type: 'STRING',
		choices: [{ name: '' }],
	},
};

export const schema = yup.object().shape({
	// The ONE real backend mirror: POST/PUT /custom-fields returns 422 when ticket_types_ids is empty.
	// Surfaced inline under the ticket picker (RightForm) so it guides rather than only disabling submit.
	ticket_types_ids: yup.array().min(1, i18n.t('customFields-ticketTypes-required', 'Pick at least one ticket type.')),
	custom_field: yup.object().shape({
		// The question text is only a real field for custom questions. For the built-in
		// "Each attendee → Name / Email" answers (ATTENDEE_NAME / ATTENDEE_EMAIL) no text
		// input is rendered — `createRequestBody` fills `name` from `name_type` — so requiring
		// it unconditionally blocked submission with an error that had nowhere to render.
		name: yup.string().when('name_type', {
			is: 'CUSTOM_QUESTION',
			then: yup.string().required(i18n.t('customFields-question-required')),
			otherwise: () => yup.string(),
		}),
		// Dropdown / multiple-choice options must be non-empty and unique (UX guard — the backend accepts
		// empty/duplicate). Only relevant in registration mode; the Type select that exposes these is
		// hidden for normal events, so this is a no-op there.
		choices: yup.array().when('type', {
			is: (type: string) => type === 'DROPDOWN' || type === 'MULTIPLE_CHOICE',
			then: (s) =>
				s
					.of(
						yup.object().shape({
							name: yup
								.string()
								.trim()
								.required(i18n.t('customFields-choice-required', 'Enter an option or remove it.')),
						})
					)
					.min(1, i18n.t('customFields-choices-min', 'Add at least one option.'))
					.test('unique-choices', i18n.t('customFields-choices-unique', 'Options must be unique.'), (choices) => {
						const names = (choices ?? []).map((c) => (c?.name ?? '').trim().toLowerCase()).filter(Boolean);
						return new Set(names).size === names.length;
					}),
			otherwise: (s) => s,
		}),
	}),
});

export const createRequestBody = (body: CustomFieldFormProps) => {
	if (body.custom_field.name_type !== 'CUSTOM_QUESTION') {
		body.custom_field.name = body.custom_field.name_type;
		body.custom_field.is_required = 'required';
	}

	return {
		...body,
		custom_field: {
			...body.custom_field,
			is_required: body.custom_field.is_required === 'required',
			only_buyer: body.custom_field.only_buyer === 'buyerOnly',
		},
	};
};

export const editFormValues = (data: CustomFieldType) => {
	const name_type = ['ATTENDEE_NAME', 'ATTENDEE_EMAIL'].includes(data.name) ? data.name : 'CUSTOM_QUESTION';
	const choices = data.choices?.length ? data.choices.map(({ name }) => ({ name })) : [{ name: '' }];

	return {
		ticket_types_ids: data.ticket_types.map(({ ID }: TicketType) => ID) || [],
		custom_field: {
			...defaultValues.custom_field,
			name_type,
			name: name_type === 'CUSTOM_QUESTION' ? data.name : '',
			type: data.type,
			is_required: data.is_required ? 'required' : 'notRequired',
			only_buyer: data.only_buyer ? 'buyerOnly' : 'eachAtendee',
			choices,
		},
	};
};
